import {
  ADMIN_CLOSED_WORK_ITEM_LABEL,
  COACH_WELCOME_TITLE,
  type CoachHistoryRow,
  type WorkItemDisposition,
  type WorkQueueItemKind,
} from "@care-by-ella/contracts";
import { isoWeek } from "./checkin-list";

// TES-1207 — the coach's Historik as lines. Pure, so the wording and the
// order run under `node --test`.
//
// A-01 names what the list holds ("tidigare coachkontakter, formulär,
// återkopplingar, överenskommelser och uppföljningar i begriplig tidsordning")
// but no row wording. The check-in words are A-01's own statuses
// ("Veckoincheckning · vecka NN", "Besvarad", "Svar skickat", "Avslutad utan
// svar"); the section names are the work queue's. The rest is proposed copy.

// The outcome of a closed case, in the words a coach used to pick it.
// Moved here from WorkItemCard so the card and Historik say the same thing.
export const DISPOSITION_LABEL: Record<WorkItemDisposition, string> = {
  closed_with_reply: "Kontaktad",
  closed_without_action: "Avslutad utan kontakt",
  escalated: "Lämnad över",
  deferred: "Uppföljning senare",
  // No coach picked this one — the patient checked in again and closed it.
  patient_returned: "Patienten återkom",
  // TES-1400 (Ö-410) — an admin's "Stäng arbetsmoment".
  closed_administratively: ADMIN_CLOSED_WORK_ITEM_LABEL,
};

// The coach's case kinds that reach Historik (COACH_HISTORY_CASE_KINDS),
// named as the queue names them.
const CASE_TITLE: Partial<Record<WorkQueueItemKind, string>> = {
  needs_follow_up_contact: "Behöver återkontakt",
  coach_welcome: COACH_WELCOME_TITLE,
  follow_up: "Uppföljning",
};

export type CoachHistoryLine = {
  id: string;
  at: string;
  // What happened, first: the row's identity.
  title: string;
  // How it ended, when the row has an outcome.
  outcome: string | null;
  // The coach's own words or the agreement, quoted.
  text: string | null;
  // "Orsak" for a close reason, so a reason never reads as a message sent.
  textLabel: string | null;
  by: string | null;
};

function checkinTitle(submittedAt: string | undefined): string {
  return submittedAt
    ? `Veckoincheckning · vecka ${isoWeek(submittedAt)}`
    : "Veckoincheckning";
}

export function historyLine(row: CoachHistoryRow): CoachHistoryLine {
  const base = { id: row.id, at: row.at, by: row.by, text: row.text };
  switch (row.kind) {
    case "coach_message":
      return {
        ...base,
        title: "Meddelande i chatten",
        outcome: null,
        textLabel: null,
      };
    case "checkin_answered":
      return {
        ...base,
        title: checkinTitle(row.submittedAt),
        outcome: "Besvarad · svar skickat",
        textLabel: null,
      };
    case "checkin_closed":
      return {
        ...base,
        title: checkinTitle(row.submittedAt),
        outcome: "Avslutad utan svar",
        textLabel: row.text ? "Orsak" : null,
      };
    case "case_closed":
      return {
        ...base,
        title: (row.itemKind && CASE_TITLE[row.itemKind]) ?? "Coachärende",
        outcome: row.disposition ? DISPOSITION_LABEL[row.disposition] : null,
        textLabel: row.text ? "Orsak" : null,
      };
    case "follow_up_completed":
      return {
        ...base,
        title: "Uppföljning gjord",
        outcome: null,
        textLabel: null,
      };
    case "agreement":
      return {
        ...base,
        title: "Överenskommelse",
        outcome: null,
        textLabel: null,
      };
    case "handoff_resolved":
      return {
        ...base,
        title:
          row.handoffDirection === "to_coach"
            ? "Överlämning till dig"
            : "Överlämning från coach",
        outcome: "Avslutad",
        textLabel: null,
      };
  }
}

// Newest first: the coach opens Historik to see what was said last. A tie
// keeps a stable order rather than flickering between reloads.
export function historyLines(rows: CoachHistoryRow[]): CoachHistoryLine[] {
  return [...rows]
    .sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id))
    .map(historyLine);
}
